import { useState, type MouseEvent } from 'react';
import { CircularProgress, Popover, Typography } from '@mui/material';
import styled from 'styled-components';
import { ReactComponent as CloseIcon } from '@elementary/common-ui/assets/close.svg';
import { ReactComponent as LinkIcon } from '@elementary/common-ui/assets/link.svg';
import { Flex, IconButton, SvgIcon } from '@elementary/common-ui/components';
import type { TicketSelection } from 'shared/hooks/useTicketSearch';
import type { TicketingIntegration } from 'shared/types';
import { ticketingIconMap } from 'shared/components/ticketing/utils';
import {
  fontSize,
  radius,
  space,
  shadow,
} from '@elementary/common-ui/style/utils';
import ConnectTicketSearchPanel from './ConnectTicketSearchPanel';

const TicketRow = styled(Flex)`
  align-items: center;
  gap: ${space('sm')};
  padding: ${space('xxsPlus', 'smMinus')};
  height: 28px;
  border-radius: ${radius('xs')};
  border: 1px solid ${({ theme }) => theme.palette.border.default};
  justify-content: space-between;
  width: fit-content;
`;

const ConnectTicketRow = styled(TicketRow)`
  color: ${({ theme }) => theme.palette.content.translucent.secondary};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.palette.surface.translucent.hover};
  }
`;

const SearchPopover = styled(Popover)<{ $placement: 'above' | 'below' }>`
  .MuiPaper-root {
    margin-top: ${({ $placement }) =>
      $placement === 'above' ? '-8px' : space('xs')};
    overflow: hidden;
    border-radius: ${radius('lg')};
    border: 1px solid ${({ theme }) => theme.palette.border.light};
    box-shadow: ${shadow('mdInk')};
  }
`;

export type ConnectedTicket = {
  key: string;
  integrationType?: string;
};

type Props = {
  envId: string;
  enabledIntegrations: TicketingIntegration[];
  isAttaching?: boolean;
  isCreatingTicket?: boolean;
  onSelect: (
    integration: TicketingIntegration,
    ticket: TicketSelection,
  ) => void | Promise<void>;
  onCreateClick?: (integration: TicketingIntegration) => void;
  selectedTicket?: ConnectedTicket | null;
  onClearSelected?: () => void;
  popoverPlacement?: 'above' | 'below';
};

const ConnectTicketDropdown = ({
  envId,
  enabledIntegrations,
  isAttaching = false,
  isCreatingTicket = false,
  onSelect,
  onCreateClick,
  selectedTicket,
  onClearSelected,
  popoverPlacement = 'below',
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClearClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!onClearSelected) return;
    onClearSelected();
  };

  const triggerIcon = selectedTicket?.integrationType
    ? ticketingIconMap[selectedTicket.integrationType]
    : null;
  const triggerLabel = selectedTicket?.key ?? 'Connect ticket';

  return (
    <>
      <ConnectTicketRow
        onClick={(e) => {
          if (isAttaching || isCreatingTicket) return;
          handleOpen(e);
        }}
      >
        <Flex alignItems="center" gap={1}>
          {isAttaching ? (
            <CircularProgress size={14} />
          ) : selectedTicket && triggerIcon ? (
            <SvgIcon width={16}>{triggerIcon}</SvgIcon>
          ) : (
            <SvgIcon width={16} fillColor="icon.tertiary">
              <LinkIcon />
            </SvgIcon>
          )}
          <Typography
            sx={{ color: 'content.secondary', fontSize: fontSize('md') }}
          >
            {triggerLabel}
          </Typography>
        </Flex>
        {selectedTicket && onClearSelected ? (
          <IconButton
            size="small"
            aria-label="Clear ticket"
            onClick={handleClearClick}
          >
            <SvgIcon width={12} height={12} strokeColor="icon.tertiary">
              <CloseIcon />
            </SvgIcon>
          </IconButton>
        ) : null}
      </ConnectTicketRow>
      <SearchPopover
        $placement={popoverPlacement}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={
          popoverPlacement === 'above'
            ? { vertical: 'top', horizontal: 'left' }
            : { vertical: 'bottom', horizontal: 'left' }
        }
        transformOrigin={
          popoverPlacement === 'above'
            ? { vertical: 'bottom', horizontal: 'left' }
            : { vertical: 'top', horizontal: 'left' }
        }
        slotProps={{ paper: { elevation: 0 } }}
      >
        <ConnectTicketSearchPanel
          envId={envId}
          enabledIntegrations={enabledIntegrations}
          isActive={open}
          isAttaching={isAttaching}
          isCreatingTicket={isCreatingTicket}
          onSelect={onSelect}
          onCreateClick={onCreateClick}
          onClose={handleClose}
        />
      </SearchPopover>
    </>
  );
};

export default ConnectTicketDropdown;
